// public/js/modules/analytics.js
// Lightweight, privacy-scoped activity collector: reports the CURRENT user's own
// in-app activity (which page, how long, active/idle, device) so superadmins get
// a presence + analytics view. Identity is taken server-side from the token; we
// never send who we are. See docs/user-analytics.md.

import { apiUrl } from './auth.js';

const HEARTBEAT_MS = 30000;   // presence refresh cadence
const IDLE_MS = 60000;        // no interaction for this long => "idle"

const device = /Mobi|Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
let currentPage = pageOf();
let enteredAt = Date.now();
let lastInteraction = Date.now();
let timer = null;
let started = false;

function pageOf() { return (location.hash || '#map').split('?')[0] || '#map'; }
function isActive() { return document.visibilityState === 'visible' && (Date.now() - lastInteraction) < IDLE_MS; }

// Best-effort ping via the normal authenticated path (ignored on failure).
function sendPing(event) {
  const body = JSON.stringify({ page: currentPage, active: isActive(), device, event: event || undefined });
  try {
    window.authenticatedFetch?.('api/v1/analytics/ping', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body,
    })?.catch(() => {});
  } catch { /* ignore */ }
}

// On unload we can't rely on a normal fetch completing — use keepalive with the
// token pulled directly (sendBeacon can't set the Authorization header).
function sendBeaconPing(event) {
  try {
    const token = JSON.parse(localStorage.getItem('superb_tracking_auth') || '{}').access_token;
    if (!token) return;
    // Same resolution as authenticatedFetch — this is the one call that cannot
    // go through it, because the token has to be read synchronously on unload.
    const url = apiUrl('api/v1/analytics/ping');
    fetch(url, {
      method: 'POST', keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ page: currentPage, active: false, device, event: event || undefined }),
    }).catch(() => {});
  } catch { /* ignore */ }
}

function dwellEvent() {
  return { page: currentPage, durationMs: Date.now() - enteredAt, at: new Date(enteredAt).toISOString() };
}

function onRouteChange() {
  const next = pageOf();
  if (next === currentPage) return;
  const ev = dwellEvent();        // close out the page we're leaving
  currentPage = next;
  enteredAt = Date.now();
  sendPing(ev);                   // reports the dwell AND the new presence page
}

export function initAnalytics() {
  if (started) return;
  started = true;
  currentPage = pageOf();
  enteredAt = Date.now();
  lastInteraction = Date.now();

  ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'].forEach(evt =>
    window.addEventListener(evt, () => { lastInteraction = Date.now(); }, { passive: true }));

  window.addEventListener('hashchange', onRouteChange);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') sendPing(dwellEvent()); // bank the time so far
    else { enteredAt = Date.now(); lastInteraction = Date.now(); sendPing(); }
  });

  window.addEventListener('pagehide', () => sendBeaconPing(dwellEvent()));

  sendPing();                                  // initial presence
  timer = setInterval(() => sendPing(), HEARTBEAT_MS);
}

export function stopAnalytics() {
  if (timer) clearInterval(timer);
  timer = null; started = false;
}
